import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MessageCircle, Lock, Eye, EyeOff } from "lucide-react";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { forgetMyAd, getMyAdAuth, getSavedUserEmail, rememberMyAd } from "@/lib/myAds";

const TOKEN_KEY = (roomId) => `stb_chat_token_${roomId}`;

export default function ListingChatsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const passedAuth = location.state?.auth || null;
  const storedAuth = getMyAdAuth(id);
  const bootAuth = passedAuth?.token || passedAuth?.contact_email
    ? passedAuth
    : storedAuth;

  const [auth, setAuth] = useState(bootAuth || { contact_email: "", owner_password: "" });
  const [needsAuth, setNeedsAuth] = useState(!bootAuth);
  const [chats, setChats] = useState(null);
  const [listingTitle, setListingTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async (authBody) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/chats/for-listing", { listing_id: id, auth: authBody });
      setChats(data.items);
      setListingTitle(data.listing_title || "");
      // Prefer token for subsequent calls; persist if we have one from earlier unlock.
      const nextAuth = authBody.token
        ? { token: authBody.token }
        : authBody;
      setAuth(nextAuth);
      setNeedsAuth(false);
      if (authBody.token) {
        rememberMyAd(id, { manage_token: authBody.token });
      } else if (authBody.contact_email) {
        // Email+password unlock: fetch manage token via verify so we can stay signed in.
        try {
          const { data: verified } = await api.post(`/listings/${id}/verify`, authBody);
          const manageToken = verified?.listing?.manage_token;
          if (manageToken) {
            setAuth({ token: manageToken });
            rememberMyAd(id, {
              manage_token: manageToken,
              contact_email: verified.listing.owner_email || authBody.contact_email,
            });
          } else {
            rememberMyAd(id, { contact_email: authBody.contact_email });
          }
        } catch (_) {
          rememberMyAd(id, { contact_email: authBody.contact_email });
        }
      }
    } catch (e) {
      if (authBody?.token && !authBody?.owner_password) {
        forgetMyAd(id);
      }
      setError(apiError(e));
      setNeedsAuth(true);
      setChats(null);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const authToTry = location.state?.auth || getMyAdAuth(id);
    if (authToTry?.token || (authToTry?.contact_email && authToTry?.owner_password)) {
      load(authToTry);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openChat = (item) => {
    localStorage.setItem(TOKEN_KEY(item.room_id), item.chat_token);
    navigate(`/zpravy/${item.room_id}`);
  };

  if (needsAuth) {
    return <UnlockScreen onUnlock={load} busy={busy} error={error} />;
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        <button onClick={() => navigate(`/sprava-inzeratu/${id}`)} className="stb-btn stb-btn-ghost text-[#0E0E0E] mb-3">
          <ArrowLeft size={14} /> Zpět na správu
        </button>
        <h1 className="stb-h text-3xl text-[#0E0E0E]">Zprávy k inzerátu</h1>
        {listingTitle && <p className="text-sm text-[#0E0E0E] mt-1">{listingTitle}</p>}

        <div className="stb-card mt-4 divide-y-2 divide-[#262626]" data-testid="listing-chats">
          {chats == null && <div className="p-4 stb-label">Načítám…</div>}
          {chats && chats.length === 0 && (
            <div className="p-6 text-center" data-testid="no-chats">
              <MessageCircle className="mx-auto text-[#A3A3A3]" size={32} />
              <p className="stb-h text-white mt-2">Zatím žádné zprávy</p>
              <p className="text-[#A3A3A3] text-sm mt-1">Až tě někdo osloví, uvidíš to tady.</p>
            </div>
          )}
          {chats && chats.map((c) => (
            <button
              key={c.room_id}
              onClick={() => openChat(c)}
              className="w-full p-3 flex items-center gap-3 hover:bg-[#1a1a1a] transition-colors text-left"
              data-testid={`chat-item-${c.room_id}`}
            >
              {c.listing_photo ? (
                <img src={c.listing_photo} alt="" className="w-14 h-14 object-cover border border-[#262626]" />
              ) : (
                <div className="w-14 h-14 bg-[#0E0E0E] border border-[#262626] inline-flex items-center justify-center">
                  <MessageCircle size={18} className="text-[#A3A3A3]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white stb-h text-base truncate">{c.buyer_name || "Zájemce"}</p>
                <p className="stb-mono text-xs text-[#A3A3A3] truncate">{c.buyer_email}</p>
              </div>
              <span className="stb-mono text-xs text-[#A3A3A3]">
                {c.last_message_at && new Date(c.last_message_at).toLocaleString("cs-CZ", {
                  day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
                })}
              </span>
            </button>
          ))}
        </div>
      </div>
    </Layout>
  );
}

function UnlockScreen({ onUnlock, busy, error }) {
  const [email, setEmail] = useState(() => getSavedUserEmail());
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    onUnlock({ contact_email: email, owner_password: pw });
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto stb-card p-6" data-testid="chats-unlock">
        <h1 className="stb-h text-2xl text-white inline-flex items-center gap-2">
          <Lock size={20} className="text-[#FF6B00]" /> Zprávy k inzerátu
        </h1>
        <p className="text-sm text-[#A3A3A3] mt-1">
          Zadej e-mail a heslo, které jsi použil/a při vložení inzerátu, abys zobrazil/a všechny příchozí zprávy.
        </p>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <input className="stb-input" type="email" placeholder="Tvůj e-mail" required
            value={email} onChange={(e) => setEmail(e.target.value)} data-testid="chats-unlock-email" />
          <div className="relative">
            <input className="stb-input pr-12" type={showPw ? "text" : "password"}
              placeholder="Heslo k inzerátu" required value={pw}
              onChange={(e) => setPw(e.target.value)} data-testid="chats-unlock-password" />
            <button type="button" onClick={() => setShowPw((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] hover:text-[#FF6B00]" tabIndex={-1}>
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {error && <p className="text-[#FF6B00] text-sm" data-testid="chats-unlock-error">{error}</p>}
          <button className="stb-btn stb-btn-primary w-full" disabled={busy} data-testid="chats-unlock-submit">
            {busy ? "Ověřuji…" : "Zobrazit zprávy"}
          </button>
        </form>
      </div>
    </Layout>
  );
}
